import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {Button, CircularProgress, Divider, IconButton, Tooltip} from '@mui/material';
import {Close, InfoOutlined, PhoneAndroid, ReceiptLong, Send, WarningAmber} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {RootState} from '../../../../store';
import api from '../../../../api/axios';
import {Trade, ViewMode, wearMap} from '../../../../types/trades';
import {RARITY_COLORS, RarityGrade} from '../../../utils/RarityColors';
import {formatMoney, moneyFromMinor} from '../../../utils/UnitConversion.utils';
import TradeStatusStepper from './TradeStatusStepper';
import ItemDetailLightModal from '../../../skin-db/offer-details/ItemDetailLightModal';
import {toast} from 'react-toastify';

type CardProps = {
  trade: Trade;
  mode: ViewMode;
  now: Date;
  isExtensionInstalled: boolean;
  mobileConfirmationLoading?: boolean;
  onOpenMobileConfirmation?: () => void | Promise<void>;
  onCancel: (trade: Trade) => void;
  onSuccess: (tradeId: string, newStatus: string, localDate: Date) => void;
  hideActionPanel?: boolean;
};

const TradeItemCard: React.FC<CardProps> = ({
                                              trade,
                                              mode,
                                              now,
                                              isExtensionInstalled,
                                              mobileConfirmationLoading = false,
                                              onOpenMobileConfirmation,
                                              onCancel,
                                              onSuccess,
                                              hideActionPanel = false
                                            }) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [lightModalOpen, setLightModalOpen] = useState(false);

  const currencyRate = useSelector((state: RootState) => state.currency.rate);
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);
  const tradeGroups = useSelector((state: RootState) => state.tradeGroup.groups || {});

  const effectiveStatus = trade.effectiveTradeStatus;
  const group = trade.tradeGroupId ? tradeGroups[trade.tradeGroupId] : null;
  const cancelType = group?.cancelType;

  const NINETY_MIN_MS = 90 * 60 * 1000;
  const TWENTY_FOUR_H_MS = 24 * 60 * 60 * 1000;
  const TWO_H_MS = 2 * 60 * 60 * 1000;

  const isExpired = effectiveStatus.endsWith('_CANCELLED');

  const isWaitingConfirm = effectiveStatus === 'WAITING_FOR_SELLER_CONFIRM';
  const isWaitingSend = effectiveStatus === 'WAITING_FOR_SELLER_SEND';
  const isWaitingAccept = effectiveStatus === 'WAITING_FOR_BUYER_ACCEPT';
  const isWaitingVerification = effectiveStatus === 'WAITING_FOR_VERIFICATION';
  const sellerLoginSecureVerificationRequired = effectiveStatus === 'LOGIN_SECURE_SELLER_VERIFICATION_REQUIRED';
  const buyerLoginSecureVerificationRequired = effectiveStatus === 'LOGIN_SECURE_BUYER_VERIFICATION_REQUIRED';

  // Helper flag for states where seller is waiting for buyer or system processing
  const isWaitingVerificationOrAccept =
      isWaitingAccept ||
      isWaitingVerification ||
      sellerLoginSecureVerificationRequired ||
      buyerLoginSecureVerificationRequired;

  const showMobileConfirmationAction =
      isWaitingSend &&
      trade.effectiveSteamTradeOfferStatus === 'CREATEDNEEDSConfirmation';

  const groupDate = group?.statusUpdateDate;
  const timerBaseDate = ((isWaitingSend || isWaitingVerificationOrAccept) && groupDate)
      ? new Date(groupDate)
      : new Date(trade.creationDate);

  const timeLimit = (isWaitingConfirm || isWaitingVerificationOrAccept) ? TWENTY_FOUR_H_MS : TWO_H_MS;
  const diffMs = now.getTime() - timerBaseDate.getTime();
  const autoCancelTimeLeftMs = Math.max(0, timeLimit - diffMs);

  // Dynamic check: true if the time is up
  const isTimerExpired = autoCancelTimeLeftMs <= 0;

  const buyerDiffMs = now.getTime() - new Date(trade.creationDate).getTime();
  const cancelTimeLeftMs = Math.max(0, NINETY_MIN_MS - buyerDiffMs);
  const isFreeCancel = buyerDiffMs >= NINETY_MIN_MS;

  const isSeller = mode === 'sold';
  const isBuyer = mode === 'purchased';
  const {catalog, details} = trade.item;
  const rarityColor = RARITY_COLORS[details?.rarity as RarityGrade] ?? '#ffffff';
  const displayPrice = formatMoney(moneyFromMinor(trade.priceMinor) * currencyRate);

  // Use simple local state timeout for guaranteed 10s cooldown ticks
  useEffect(() => {
    if (cooldownLeft <= 0) return;

    const timer = setTimeout(() => {
      setCooldownLeft(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [cooldownLeft]);

  const formatMs = (ms: number, showHours = false) => {
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = showHours
        ? Math.floor((totalSeconds % 3600) / 60)
        : Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;

    return showHours && h > 0
        ? `${h}h ${m}m ${s}s`
        : `${m}m ${s}s`;
  };

  const handleAcceptTrade = async () => {
    if (isTimerExpired || loading) return;

    setLoading(true);

    try {
      const response = await api.post(`/user/trade/accept`, {
        tradeIds: [trade.id]
      });

      if (response.status === 200) {
        onSuccess(trade.id, 'WAITING_FOR_SELLER_SEND', new Date());
      }
    } catch (e) {
      console.error('Accept failed', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSendItem = async () => {
    // Explicit hard block if component is in cooldown or loading
    if (!isExtensionInstalled || isTimerExpired || cooldownLeft > 0 || loading) return;

    setLoading(true);

    try {
      const response = await api.get(`/user/trade/send-details`, {
        params: {tradeIds: [trade.id]}
      });

      const tradeData = response.data;

      let normalizedAssetIds: string[] = [];

      if (Array.isArray(tradeData.itemAssetId)) {
        normalizedAssetIds = tradeData.itemAssetId.flatMap((id: string) =>
            typeof id === 'string' ? id.split(',') : String(id)
        );
      } else if (typeof tradeData.itemAssetId === 'string') {
        normalizedAssetIds = tradeData.itemAssetId.split(',');
      } else if (tradeData.itemAssetId) {
        normalizedAssetIds = [String(tradeData.itemAssetId)];
      }

      const payload = {
        tradeGroupId: tradeData.tradeGroupId,
        toSteamID64: tradeData.buyerSteamId,
        tradeToken: tradeData.tradeToken,
        message: tradeData.message,
        assetIDsToGive: normalizedAssetIds,
        assetIDsToReceive: [],
        forceEnglish: true
      };

      // Define message handler before posting to ensure synchronous responses are caught
      const handleExtensionResponse = (event: MessageEvent) => {
        if (
            event.data?.target === 'MOONSKINS_WEBAPP' &&
            event.data?.action === 'OFFER_CREATED'
        ) {
          window.removeEventListener('message', handleExtensionResponse);

          if (event.data.success) {
            // Update UI/Backend status
            onSuccess(trade.id, 'WAITING_FOR_BUYER_ACCEPT', new Date());
            setLoading(false);
          } else {
            // Handle extension-level errors (e.g. Steam Session expired)
            console.error('Extension failed to create offer:', event.data.error);
            toast.error(t('Failed to send item'));
            // Start local cooldown state simultaneously with disabling loading
            setCooldownLeft(10);
            setLoading(false);
          }
        }
      };

      window.addEventListener('message', handleExtensionResponse);

      // Post message to extension after listener setup
      window.postMessage({
        target: 'MOONSKINS_EXTENSION',
        version: 1,
        request_type: 11,
        payload,
        id: Math.floor(Math.random() * 1000000)
      }, '*');

      // Cleanup listener after 30 seconds to prevent memory leaks
      setTimeout(() => {
        window.removeEventListener('message', handleExtensionResponse);

        setLoading(currentLoading => {
          if (currentLoading) {
            setCooldownLeft(10);
          }

          return false;
        });
      }, 30000);
    } catch (e) {
      console.error('Failed to fetch trade details from backend', e);
      setCooldownLeft(10);
      setLoading(false);
    }
  };

  const handleMobileConfirmation = async () => {
    if (loading || mobileConfirmationLoading || !onOpenMobileConfirmation) {
      return;
    }

    await onOpenMobileConfirmation();
  };

  return (
      <>
        <div className="shadow-md rounded-lg p-5 bg-white dark:bg-neutral-800/50 border border-gray-100 dark:border-white/5">
          <div className="flex justify-between items-start">
            <div
                onClick={() => setLightModalOpen(true)}
                className="flex items-center gap-4 cursor-pointer group/item"
            >
              <div className="relative flex items-center">
                <div style={{width: 4, height: 56, background: rarityColor}}/>
                <div className="relative ml-1">
                  <img
                      src={catalog?.imageLink ?? ''}
                      className="w-14 h-14 object-contain transition-transform duration-200 group-hover/item:scale-105"
                      alt="item"
                  />
                  <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: -4,
                        transform: 'translateY(-50%)',
                        width: 56,
                        height: 56,
                        background: `linear-gradient(to right, ${rarityColor}33, transparent)`
                      }}
                  />
                </div>
              </div>

              <div>
                <p className="text-sm font-bold text-gray-800 dark:text-gray-100 group-hover/item:text-dark-primary transition-colors">
                  {catalog?.groupName ?? 'Unknown Item'}
                </p>

                <div className="flex gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                  {catalog?.isStattrak && (
                      <span className="text-orange-500 font-bold">ST</span>
                  )}
                  {catalog?.isSouvenir && (
                      <span className="text-yellow-600 font-bold">SN</span>
                  )}
                  {catalog?.wearName && (
                      <span>{wearMap[catalog.wearName] ?? ''}</span>
                  )}
                </div>

                <p className="text-xs font-mono font-bold text-blue-600 dark:text-gray-400 mt-0.5">
                  {currencySymbol}{displayPrice}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <Tooltip title={t('history.tooltips.view_transaction')} arrow>
                  <IconButton
                      onClick={() => navigate(`/profile/transactions?id=${trade.id}`)}
                      size="small"
                      className="!p-1 !bg-gray-50 dark:!bg-white/5 hover:!bg-blue-50 dark:hover:!bg-blue-500/10 !text-gray-400 hover:!text-blue-500 transition-colors"
                      sx={{borderRadius: '6px'}}
                  >
                    <ReceiptLong sx={{fontSize: 16}}/>
                  </IconButton>
                </Tooltip>

                <span className={`text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded-full ${
                    effectiveStatus === 'COMPLETED'
                        ? 'text-green-600 bg-green-50 dark:text-green-500 dark:bg-green-300/10'
                        : isExpired
                            ? 'text-red-600 bg-red-50 dark:text-red-500 dark:bg-red-300/10'
                            : 'text-blue-600 bg-blue-50 dark:text-dark-primary dark:bg-dark-primary/10'
                }`}>
                              {effectiveStatus === 'COMPLETED'
                                  ? t('history.status.successful')
                                  : isExpired
                                      ? t('history.status.canceled')
                                      : t('history.status.in_progress')}
                          </span>
              </div>

              {isExpired && cancelType && (
                  <span className="text-[9px] text-gray-400 font-bold uppercase tracking-tight mr-1">
                              {t(`history.cancel_types.${cancelType}`)}
                          </span>
              )}
            </div>
          </div>

          {/* Action Panel: Rendered only if not hidden by parent layout settings */}
          {!hideActionPanel && !isExpired && (isWaitingConfirm || isWaitingSend || isWaitingVerificationOrAccept || showMobileConfirmationAction) && (
              <div className="mt-4 p-4 bg-gray-50 dark:bg-neutral-900/50 rounded-lg flex justify-between items-center border border-gray-100 dark:border-white/5">
                <div className="w-1/3">
                  <p className="font-black text-gray-800 dark:text-gray-100 text-[10px] uppercase tracking-wider">
                    {isSeller
                        ? (isWaitingConfirm
                            ? t('history.actions.action_required')
                            : showMobileConfirmationAction
                                ? t('history.actions.action_required')
                                : isWaitingVerificationOrAccept
                                    ? t('history.actions.waiting_acceptance', { defaultValue: 'Waiting' })
                                    : t('history.actions.send_required'))
                        : t('history.actions.seller_notified')}
                  </p>

                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 font-medium leading-tight">
                    {isSeller
                        ? (isWaitingConfirm
                            ? t('history.actions.confirm_proceed')
                            : showMobileConfirmationAction
                                ? 'Confirm the Steam trade on your mobile device'
                                : isWaitingVerificationOrAccept
                                    ? t('history.actions.waiting_acceptance_desc', { defaultValue: 'Waiting for partner or system verification' })
                                    : t('history.actions.open_steam_send'))
                        : (isWaitingConfirm
                            ? t('history.actions.waiting_acceptance')
                            : t('history.actions.waiting_delivery'))}
                  </p>
                </div>

                <div className="flex flex-col items-center flex-1 border-l border-r border-gray-200 dark:border-white/5 px-2">
                          <span className="text-[9px] text-gray-400 dark:text-gray-500 uppercase font-black tracking-tighter">
                              {t('history.actions.expires_in')}
                          </span>

                  <span className={`text-sm font-mono font-black ${
                      autoCancelTimeLeftMs < 600000
                          ? 'text-red-500 animate-pulse'
                          : 'text-gray-800 dark:text-gray-200'
                  }`}>
                              {formatMs(autoCancelTimeLeftMs, true)}
                          </span>
                </div>

                <div className="w-1/3 flex flex-col items-end pl-4 relative">
                  {/* Hide action container completely if timer is expired or waiting for accept/verification for seller */}
                  {isSeller && !isTimerExpired && !isWaitingVerificationOrAccept && (
                      <div className="flex flex-col items-end w-full min-w-[160px] max-w-[220px]">
                        <div className="flex items-center gap-2 w-full">
                          <Tooltip
                              title={!isExtensionInstalled ? t('history.tooltips.extension_required') : ''}
                              arrow
                              placement="top"
                          >
                                          <span className="flex-1">
                                              {isWaitingConfirm ? (
                                                  <Button
                                                      variant="contained"
                                                      size="small"
                                                      disabled={!isExtensionInstalled || loading}
                                                      className="!text-white !font-bold w-full"
                                                      onClick={handleAcceptTrade}
                                                      sx={{
                                                        height: '38px',
                                                        borderRadius: '8px',
                                                        boxShadow: 'none'
                                                      }}
                                                  >
                                                    {loading
                                                        ? <CircularProgress size={20} color="inherit"/>
                                                        : t('history.actions.accept_sale')}
                                                  </Button>
                                              ) : showMobileConfirmationAction ? (
                                                  <Tooltip title="Confirm trade on Steam Mobile" arrow>
                                                      <span className="flex justify-end">
                                                          <IconButton
                                                              onClick={handleMobileConfirmation}
                                                              disabled={loading || mobileConfirmationLoading}
                                                              className="!bg-blue-500 !text-white hover:!bg-blue-600 !flex-shrink-0"
                                                              size="small"
                                                              sx={{
                                                                borderRadius: '8px',
                                                                height: '38px',
                                                                width: '38px'
                                                              }}
                                                          >
                                                            {mobileConfirmationLoading || loading
                                                                ? <CircularProgress size={20} color="inherit"/>
                                                                : <PhoneAndroid fontSize="small"/>
                                                            }
                                                          </IconButton>
                                                      </span>
                                                  </Tooltip>
                                              ) : (
                                                  <Button
                                                      variant="contained"
                                                      color="primary"
                                                      size="small"
                                                      disabled={!isExtensionInstalled || loading || cooldownLeft > 0}
                                                      className="!text-white !font-bold w-full"
                                                      onClick={handleSendItem}
                                                      startIcon={
                                                        loading
                                                            ? <CircularProgress size={20} color="inherit"/>
                                                            : <Send fontSize="small"/>
                                                      }
                                                      sx={{
                                                        height: '38px',
                                                        borderRadius: '8px',
                                                        boxShadow: 'none'
                                                      }}
                                                  >
                                                    {loading
                                                        ? t('history.actions.sending')
                                                        : cooldownLeft > 0
                                                            ? `${t('history.actions.retry_in', {defaultValue: 'Retry in'})} (${cooldownLeft}s)`
                                                            : t('history.actions.send_item')}
                                                  </Button>
                                              )}
                                          </span>
                          </Tooltip>

                          <IconButton
                              onClick={() => onCancel(trade)}
                              color="error"
                              disabled={loading}
                              className="!bg-red-500 !text-white hover:!bg-red-600 !flex-shrink-0"
                              size="small"
                              sx={{
                                borderRadius: '8px',
                                height: '38px',
                                width: '38px'
                              }}
                          >
                            <Close fontSize="small"/>
                          </IconButton>
                        </div>

                        <div className="absolute top-[31px] right-0 w-full flex justify-end">
                          <Tooltip title={t('history.tooltips.seller_penalty_info')} arrow>
                            <p className="text-[9px] text-red-500 dark:text-red-400 font-black uppercase tracking-tighter flex items-center mt-1 whitespace-nowrap">
                              <WarningAmber className="!w-3 mr-1"/>
                              {t('history.actions.penalty_risk')}
                            </p>
                          </Tooltip>
                        </div>
                      </div>
                  )}

                  {/* Fallback text if seller timer expired but backend hasn't updated status yet */}
                  {isSeller && isTimerExpired && !isWaitingVerificationOrAccept && (
                      <span className="text-[10px] font-black text-red-500 uppercase tracking-widest animate-pulse">
                                  {t('history.status.processing_expiration')}
                              </span>
                  )}

                  {isBuyer && (
                      <div className="flex flex-col items-end w-full max-w-[160px]">
                        <Button
                            variant="contained"
                            color={isFreeCancel ? 'error' : 'warning'}
                            size="small"
                            onClick={() => onCancel(trade)}
                            className="!text-white !font-bold"
                            sx={{
                              height: 38,
                              width: '100%',
                              borderRadius: '8px',
                              boxShadow: 'none'
                            }}
                        >
                          {t('history.actions.cancel_trade')}
                        </Button>

                        <div className="absolute top-[38px] right-0 w-full flex justify-end">
                          <p className="text-[9px] text-right leading-tight whitespace-nowrap flex items-center mt-1">
                            {isFreeCancel ? (
                                <span className="text-red-500 dark:text-neutral-400 font-black uppercase tracking-tighter flex items-center">
                                                  <InfoOutlined className="!w-3 mr-1"/>
                                  {t('history.actions.free_cancellation')}
                                              </span>
                            ) : (
                                <span className="text-gray-400 font-medium">
                                                  {t('history.actions.fee_info', {
                                                    time: formatMs(cancelTimeLeftMs, false)
                                                  })}
                                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                  )}
                </div>
              </div>
          )}

          {/* Display dividing structure and status track step sequence only in full un-grouped mode */}
          {!hideActionPanel && (
              <>
                <div className="my-5">
                  <Divider className="dark:!border-white/5"/>
                </div>

                <TradeStatusStepper trade={trade}/>
              </>
          )}
        </div>

        <ItemDetailLightModal
            open={lightModalOpen}
            onClose={() => setLightModalOpen(false)}
            item={trade.item as any}
        />
      </>
  );
};

export default TradeItemCard;
